/*
 * GEHC CONFIDENTIAL
 *
 * Copyright (c) 2020, 2025, GE HealthCare
 * All Rights Reserved
 *
 * This unpublished material is proprietary to GE HealthCare. The methods and
 * techniques described herein are considered trade secrets and/or
 * confidential. Reproduction or distribution, in whole or in part, is
 * forbidden except by express written permission of GE HealthCare.
 * GE is a trademark of General Electric Company used under trademark license.
 */

import * as _ from 'underscore';
import { Logger } from '@core/lib/services';
import { Injectable, Injector } from '@angular/core';
import { SearchService } from '@app/ui/search/search.service';
import { AppConfigService } from '@app/common/app-config.service';
import { EventMessage, Events, EventService } from '@app/common/event';
import { UserPreferenceService } from '@app/common/user-preference.service';
import { IntegrationStatusService } from '@app/di/integration-status.service';
import { DiService } from '@app/di/di.service';
import { ArchiveAdoptedService } from '@app/common/services/bring-online/archive-adopted.service';
import { UtilService } from '@app/util/util.service';
import { NotifyService } from '@app/common/notify.service';
import { XmlTemplateManagerService } from '@app/di/channel/lineshaft/xml-template-manager.service';
import { ContextService } from '@app/di/context.service';
import { appConstants } from '@app/common/app-constants';
import { MessageService } from '@core/lib/services/message.service';
import { TranslateService } from '@ngx-translate/core';
import { WorkItem } from '@app/data-model/item/work-item';
import { WorkItemService } from '@app/data-model/item/work-item.service';
import { EventWorkItem } from '@app/data-model/event-work-item';
import { ToggleKnobService } from '@app/ui/common/toggle-knob-service';
import { ReportContextService } from '@app/di/integrations/reporting/report-context.service';
import { REPORT_STATUS, REPORTING_EVENT } from '@app/di/integrations/reporting/reporting-contants';
import { P2pNotificationService } from '@app/common/p2p-notification.service';
import { StateUpdate } from '@app/data-model/state-update';
import { Observable, Subject } from 'rxjs';

const log = new Logger('ExternalAppContextService');

@Injectable({
  providedIn: 'root'
})
export class ExternalAppContextService {
  private requests = [];
  private externalContextEnabled;
  private examLinkWorkItem;

  private readonly _removeContext: Subject<any> = new Subject();
  public removeContext$: Observable<string> = this._removeContext.asObservable();

  constructor(
    private injector: Injector,
    private searchService: SearchService,
    private appConfigService: AppConfigService,
    private eventService: EventService,
    private userPreferenceService: UserPreferenceService,
    private integrationStatusService: IntegrationStatusService,
    private diService: DiService,
    private archiveAdoptedService: ArchiveAdoptedService,
    private notify: NotifyService,
    private contextService: ContextService,
    private xmlTemplateManagerService: XmlTemplateManagerService,
    private utilService: UtilService,
    private messageService: MessageService,
    private translate: TranslateService,
    private reportContextService: ReportContextService,
    private toggleKnobService: ToggleKnobService,
    private workItemService: WorkItemService,
    private p2pNotificationService: P2pNotificationService
  ) {
    this.broadcastListeners();
    this.registerEvents();
  }

  handleGExInboundSetcontextResponse(responseInfo) {
    this.requests.push(responseInfo);
    if (responseInfo.examIdent.indexOf('UID:') > -1) {
      responseInfo.accession = responseInfo.examIdent.split('UID:')[1];
      responseInfo.contextActionType = '0-UID';
    }
    if (responseInfo.contextActionType === appConstants.genExActionTypes.OPEN) {
      this.setContextByGenExWithAccession(responseInfo.accession).then(
        workItem => {
          if (workItem) {
            if (responseInfo.accession) {
              const tab = this.utilService.getTabByAccession(responseInfo.accession);
              if (tab && tab.workItem.keys.imageCount == 0) {
                this.acknowledgeGExInbound(
                  this.getGenExResponseData(appConstants.SUCCESS, 'Zero images exam opened, operation completed', [
                    appConstants.genExCodes.ERR_ZERO_IMAGE_STUDY
                  ]),
                  false
                );
              }
            }
          }
        },
        error => {
          if (error == appConstants.FOUND_NONE) {
            this.showExamNotFoundModal();
          } else {
            log.error(
              'ExternalAppContext - set context from GExInbound failed for accession: ' +
                responseInfo.accession +
                ', ERROR: ' +
                error
            );
            const report = {
              event: REPORTING_EVENT.OPEN,
              accession: responseInfo.accession,
              status: REPORT_STATUS.UNKNOWN,
              interrupted: false,
              result: appConstants.FAILURE
            };
            this.messageService.broadcast('publishReportEvents', report);
            this.notifyErrorResponse(error);
            this.showErrorNotification(error, responseInfo.accession);
          }
        }
      );
    } else if (
      responseInfo.contextActionType === appConstants.genExActionTypes.CLOSE_CURRENT ||
      responseInfo.contextActionType === appConstants.genExActionTypes.CLOSE ||
      responseInfo.contextActionType == appConstants.genExActionTypes.CLOSE_KEEP_ASSIGNMENT ||
      responseInfo.contextActionType == appConstants.genExActionTypes.CLOSE_DISCARD_ASSIGNMENT
    ) {
      let tab;
      if (responseInfo.accession) {
        tab = this.utilService.getTabByAccession(responseInfo.accession);
      } else {
        tab = this.utilService.getActiveTab();
      }

      if (tab) {
        tab.workItem.actionType = 'externalDefault';
        if (responseInfo.contextActionType == appConstants.genExActionTypes.CLOSE_KEEP_ASSIGNMENT) {
          tab.workItem.actionType = 'externalKeep';
        } else if (responseInfo.contextActionType == appConstants.genExActionTypes.CLOSE_DISCARD_ASSIGNMENT) {
          tab.workItem.actionType = 'externalDiscard';
        }
        tab.workItem.isTabCloseFromExternalApp = true;
        const integrationPendingErrMsg = 'Something went wrong, the exam cannot be closed';
        // Checking UV is launched for the active tab
        if (this.utilService.isTabActive(tab.id) && this.integrationStatusService.isPendingRequest()) {
          if (responseInfo.accession) {
            log.error(
              'ExternalAppContext - set context from GExInbound failed for active tab with accession: ' +
                responseInfo.accession +
                ', ERROR: ' +
                integrationPendingErrMsg
            );
          } else {
            log.error(
              'ExternalAppContext - set context from GExInbound failed for active tab' +
                ', ERROR: ' +
                integrationPendingErrMsg
            );
          }
          if (this.reportContextService.getReportContext() === tab.workItem.keys?.procedureAccessionNo) {
            const report = {
              event: REPORTING_EVENT.CLOSE,
              accession: tab.workItem.keys?.procedureAccessionNo,
              status: REPORT_STATUS.UNKNOWN,
              interrupted: false,
              result: appConstants.FAILURE
            };
            this.messageService.broadcast('publishReportEvents', report);
          }
          this.acknowledgeGExInbound(
            this.getGenExResponseData(appConstants.FAILURE, integrationPendingErrMsg, [
              appConstants.genExCodes.ERR_SYSTEM_ERROR
            ]),
            false
          );
        } else {
          this.messageService.broadcast('removeTab', tab.id);
        }
      } else {
        const errorMsg = !_.isEmpty(responseInfo.accession)
          ? 'No exam found for the accession : ' + responseInfo.accession
          : 'No exam found';
        log.error('ERROR: ' + errorMsg);
        this.acknowledgeGExInbound(
          this.getGenExResponseData(appConstants.FAILURE, errorMsg, [appConstants.genExCodes.ERR_STUDY_NOT_FOUND]),
          false
        );
      }
    } else {
      this.acknowledgeGExInbound(
        this.getGenExResponseData(appConstants.FAILURE, appConstants.genExCodes.ERR_NOT_IMPLEMENTED, [
          appConstants.genExCodes.ERR_NOT_IMPLEMENTED
        ]),
        false
      );
      log.error('ExternalAppContext - Unimplemented API received from GenEx - ' + JSON.stringify(responseInfo));
      this.notify.error(this.translate.instant('di.desktopIntegration.critical.genExUnimplemented'));
    }
  }

  private notifyErrorResponse(error: any) {
    if (error == appConstants.genExCodes.ERR_STUDY_NOT_ONLINE) {
      this.acknowledgeGExInbound(
        this.getGenExResponseData(appConstants.FAILURE, 'The exam is offline, it cannot be launched', [
          appConstants.genExCodes.ERR_STUDY_NOT_ONLINE
        ]),
        false
      );
    } else if (error == appConstants.FOUND_MORE) {
      this.acknowledgeGExInbound(
        this.getGenExResponseData(appConstants.FAILURE, 'Duplicate accession, the exam is not opened or launched', [
          appConstants.genExCodes.ERR_SYSTEM_ERROR
        ]),
        false
      );
    } else if (error == appConstants.FOUND_NONE) {
      this.acknowledgeGExInbound(
        this.getGenExResponseData(appConstants.FAILURE, 'Unknown accession, the exam is not opened or launched', [
          appConstants.genExCodes.ERR_STUDY_NOT_FOUND
        ]),
        false
      );
    } else {
      this.acknowledgeGExInbound(
        this.getGenExResponseData(
          appConstants.FAILURE,
          'Something went wrong, the exam might not been opened or launched',
          [appConstants.genExCodes.ERR_SYSTEM_ERROR]
        ),
        false
      );
    }
  }

  setContextByGenExWithAccession(accNum) {
    if (accNum) {
      accNum = accNum.split(',')[0];
    }
    return new Promise((resolve, reject) => {
      const tab = this.utilService.getTabByAccession(accNum);
      if ((!tab || !this.utilService.isTabActive(tab.id)) && this.integrationStatusService.isPendingRequest()) {
        reject(appConstants.PENDING_RESPONSE);
        return;
      }
      this.searchService.getWorkItemByAccession(accNum).then(
        (workItem: WorkItem) => {
          if (!this.integrationStatusService.canOpenNewTab(workItem.id)) {
            reject(appConstants.genExCodes.ERR_CANNOT_OPEN_MORE_TABS);
            workItem = undefined;
          }
          if (workItem) {
            const tab = this.utilService.getTab(workItem.id);
            if (tab) {
              let examContext = tab.examContext;
              if (tab.examContext !== Events.read.name && this.workItemService.canLaunchDictation(workItem)) {
                examContext = Events.read.name;
                tab.workItem.keys.launchDictation = true;
              }
              tab.workItem.keys.disableAutoLaunchViewer = false;
              tab.workItem.keys.openViewer = true;
              tab.workItem.isTabCloseFromExternalApp = false;
              this.eventService
                .sendEvent(
                  new EventMessage(Events[examContext], new EventWorkItem(tab.workItem.id, tab.workItem.keys), {
                    launchOfflinePopup: this.workItemService.isLaunchOfflinePopup(workItem)
                  })
                )
                .then(
                  () => {
                    this.processErrorOnOpenTab(workItem, { resolve, reject });
                  },
                  error => {
                    reject(error);
                  }
                );
            } else {
              workItem.setContextByGenEx = true;
              this.workItemService.activate(workItem, this.workItemService.isLaunchOfflinePopup(workItem)).then(
                () => {
                  this.toggleKnobService.openDiagnosticHub();
                  this.processErrorOnOpenTab(workItem, { resolve, reject });
                },
                error => {
                  reject(error);
                }
              );
            }
          }
        },
        error => {
          log.error('ExternalAppContext - Unable to set Context for Accession - ' + accNum + ', Error - ' + error);
          reject(error);
        }
      );
    });
  }

  setContextByAccession(accNum) {
    this.externalContextEnabled = this.appConfigService.getAppConfigByKey('externalContextEnabled') === 'true';
    return new Promise((resolve, reject) => {
      if (this.externalContextEnabled) {
        this.searchService.getWorkItemByAccession(accNum).then(
          (workItem: WorkItem) => {
            if (!this.integrationStatusService.canOpenNewTab(workItem.id, true)) {
              reject(appConstants.MAX_TAB_ERROR);
            } else {
              this.setContextByBidirectional({ resolve, reject }, workItem);
            }
          },
          error => {
            log.error('ExternalAppContext - Unable to set Context for Accession - ' + accNum + ', Error - ' + error);
            this.openEmptyExternalTab(accNum, error === appConstants.FOUND_MORE || error === appConstants.FOUND_NONE);
            reject(error);
          }
        );
      } else {
        this.openEmptyExternalTab(accNum, false);
        reject('Bi-directional is disabled');
      }
    });
  }

  setContextByExamIdentifier(launchParameters) {
    return new Promise((resolve, reject) => {
      (launchParameters.SIUID
        ? this.searchService.getWorkItemBySIUID(launchParameters.SIUID).then((workItem: WorkItem) => {
            this.examLinkWorkItem = workItem;
            log.info('ExternalAppContext - Found workitem by SIUID - ' + launchParameters.SIUID);
            this.setContextBySharedLink(this.examLinkWorkItem, resolve, reject);
          })
        : Promise.reject('No SIUID provided to launch exam')
      )
        .catch(SIUIDSearchError => {
          log.error(
            'ExternalAppContext - Unable to set Context for SIUID - ' +
              launchParameters.SIUID +
              ', Error - ' +
              SIUIDSearchError
          );
          this.searchService
            .getWorkItemByAccession(launchParameters.accession)
            .then((workItem: WorkItem) => {
              this.examLinkWorkItem = workItem;
              log.info('ExternalAppContext - Found workitem by Accession - ' + launchParameters.accession);
              this.setContextBySharedLink(this.examLinkWorkItem, resolve, reject);
            })
            .catch(accessionSearchError => {
              log.error(
                'ExternalAppContext - Unable to set Context for Accession - ' +
                  launchParameters.accession +
                  ', Error - ' +
                  accessionSearchError
              );
              reject(accessionSearchError);
            });
        })
        .finally(() => delete this.examLinkWorkItem?.keys?.eventComment);
    });
  }

  setContextBySharedLink(workItem, resolve, reject) {
    if (!workItem) {
      log.error('ExternalAppContext - No work item found after search');
      reject();
    }

    const autoLaunchReportingUserPref = this.userPreferenceService.getUserPreferenceFromCache('dictate') === true;
    const autoLaunchViewerUserPref =
      this.userPreferenceService.getUserPreferenceFromCache('autoLaunchViewerFromExternalSystem') === true;

    const tab = this.utilService.getTab(workItem.id);
    // This controls the display of the import icon.
    // If we open a new tab we have to show it.
    // If the tab has already opened with the icon, we have to keep it.
    workItem.isOpenedFromUrl = !tab || tab.workItem?.keys?.isOpenedFromUrl;
    workItem.eventComment = 'Opened via hyperlink.';
    workItem.useAutoLaunchUserPreference = true;

    let event: Promise<any>;
    if (
      this.isSMSOperationInProgress(workItem, true) ||
      UtilService.isActivatedByOther(workItem, this.appConfigService.getUserId())
    ) {
      event = this.workItemService.previewExam(workItem);
    } else if (UtilService.isAssignedToOther(workItem, this.appConfigService.getUserId())) {
      workItem.useAutoLaunchUserPreference = false; // ignore the preference, viewer launch depends on the AssignToMe dialog choice
      event = this.workItemService.launchExam(workItem);
    } else {
      //launch based on prefs
      if (autoLaunchReportingUserPref) {
        event = this.workItemService.activate(workItem).then(() => this.toggleKnobService.openDiagnosticHub());
      } else if (autoLaunchViewerUserPref) {
        event = this.workItemService.launchReadonly(workItem).then(() => this.toggleKnobService.openDiagnosticHub());
      } else {
        event = this.workItemService.previewExam(workItem);
      }
    }

    event.then(
      () => resolve(workItem),
      error => reject(error)
    );
  }

  processErrorOnOpenTab(workItem, deferred) {
    if (!this.archiveAdoptedService.isExamAvailable(workItem)) {
      deferred.reject(appConstants.genExCodes.ERR_STUDY_NOT_ONLINE);
    } else {
      deferred.resolve(workItem);
    }
  }

  setContextByBidirectional(deferred, workItem) {
    if (workItem) {
      const isActivatedByOther = !!workItem.state.isActivatedByOther;

      const receiverUser = isActivatedByOther && workItem.state.ownership.assignedToUser[0]?.userName;

      // Look for a tab which is already open in WFM
      const tab = this.utilService.getTab(workItem.id);
      if (tab) {
        if (!this.contextService.isStudyDataAvailable(workItem.id)) {
          tab.workItem.keys.disableAutoLaunchViewer = !this.userPreferenceService.getUserPreferenceFromCache(
            'autoLaunchViewerFromExternalSystem'
          );
        }

        if (tab.workItem.keys.setContextByGenEx) {
          tab.workItem.keys.disableAutoLaunchViewer = false;
        }
        tab.workItem.keys.openViewer = true;
        tab.workItem.keys.launchDictation = true;
        tab.workItem.keys.launchByExternalApp = true;

        if (isActivatedByOther) {
          tab.workItem.takeOwnership = true;
        }

        this.eventService.sendEvent(new EventMessage(Events.read, { workItem: tab.workItem })).then(
          () => {
            log.debug('Read event was successful for exam having accession - ' + workItem.accession);
            if (isActivatedByOther && !this.isSMSOperationInProgress(workItem)) {
              log.debug('Ownership transferred successfully. Accession - ' + workItem.accession);
              this.notifyOwnerShipTransfer(workItem.id, workItem.accession, receiverUser);
            }
            deferred.resolve(workItem);
          },
          error => {
            log.error('External-context service : Read event failed.');
            if (isActivatedByOther && !this.isSMSOperationInProgress(workItem)) {
              log.debug('Ownership transfer failed for accession - ' + workItem.accession);
              this.notifyFailedToAcquireOwnership(workItem.accession);
            } else {
              if (this.isSMSOperationInProgress(workItem) && this.isOperationDiscarded(workItem.accession)) {
                log.debug('SMS is in progress of exam having accession - ' + workItem.accession);
                this.notifyUserAboutSMSInprogress(workItem.accession);
              }
            }
            deferred.reject(error);
          }
        );
      } else {
        // activate the tab
        workItem.setContextByExternalApp = true;
        if (this.reportContextService.getReportContext() === workItem.accession) {
          const externalTab = this.utilService.getExternalTab();
          if (externalTab) {
            if (externalTab.id === appConstants.externalTabId) {
              externalTab.id = workItem.id;
              externalTab.workItem = this.workItemService.getEventFrameworkTarget(workItem);
              externalTab.isExternalTab = true;
            } else {
              externalTab.isExternalTab = false;
            }
          }

          if (isActivatedByOther) {
            workItem.takeOwnership = true;
          }

          this.workItemService.activate(workItem).then((transaction: any) => {
            Promise.all(transaction.getResolved).then(
              () => {
                log.debug('Success return from activate method. Accession  - ' + workItem.accession);
                this.toggleKnobService.openDiagnosticHub();
                if (isActivatedByOther && !this.isSMSOperationInProgress(workItem)) {
                  log.debug('Ownership transferred successfully. Accession - ' + workItem.accession);
                  this.notifyOwnerShipTransfer(workItem.id, workItem.accession, receiverUser);
                }
                deferred.resolve(workItem);
              },
              error => {
                log.error('Error return from activate for exam with accession - ' + workItem.accession);
                if (isActivatedByOther && !this.isSMSOperationInProgress(workItem)) {
                  log.debug('Ownership transfer failed for accession - ' + workItem.accession);
                  this.notifyFailedToAcquireOwnership(workItem.accession);
                  deferred.resolve(workItem);
                } else {
                  log.debug('SMS is in progress of exam having accession - ' + workItem.accession);
                  if (this.isSMSOperationInProgress(workItem) && this.isOperationDiscarded(workItem.accession)) {
                    let counter = 0;
                    const checker = setInterval(() => {
                      if (!this.integrationStatusService.isViewerPending()) {
                        clearInterval(checker);
                        this.messageService.broadcast('removeTab', workItem.id);
                        log.debug('Remove Tab broadcasted.');
                        return;
                      }
                      counter += 1;
                      if (counter >= 100) {
                        log.error('Viewer takes too long time to finish request. Cannot request to close tab ');
                        clearInterval(checker);
                      }
                    }, 100);
                    this.notifyUserAboutSMSInprogress(workItem.accession);
                  }
                  deferred.reject(error);
                }
              }
            );
          });
        } else {
          this.openEmptyExternalTab(undefined);
          deferred.reject();
        }
      }
    } else {
      deferred.reject();
    }
  }

  getGenExResponseData(resultValue, resultMsg, failureTypes) {
    return {
      resultValue,
      resultMsg,
      failureTypes
    };
  }

  acknowledgeGExInbound(responseInfo, isContextMismatch?) {
    if (this.requests.length > 0) {
      const request = this.requests[0];
      this.requests.splice(0, 1);
      const isFailure = isContextMismatch || responseInfo.resultValue === appConstants.FAILURE;
      if (isFailure && responseInfo.failureTypes && responseInfo.failureTypes.length < 0) {
        responseInfo.failureTypes = [appConstants.genExCodes.ERR_NOT_IMPLEMENTED];
      }
      const xml = this.xmlTemplateManagerService.getGExInboundContextXml(request, responseInfo, isFailure);
      this.diService.invokeAPI(xml);
    }
  }

  showErrorNotification(error, acc) {
    if (error === appConstants.FOUND_MORE || error === appConstants.FOUND_NONE) {
      this.notify.critical(
        this.translate.instant('di.desktopIntegration.critical.genExUnknownDuplicateExam') + acc,
        this.translate.instant('di.desktopIntegration.titleGenExCannotOpen')
      );
    }
  }
  showExamNotFoundModal(): void {
    this.notify.critical(
      this.translate.instant('collaboration.openlink.modal.recordNotAccessibleMessage'),
      this.translate.instant('collaboration.openlink.modal.recordNotAccessibleTitle')
    );
  }

  openEmptyExternalTab(accNum, isErrored?) {
    this.reportContextService.setCurrentContext(appConstants.externalTabId, accNum);
    this.messageService.broadcast('openExternalTab', { accNum: accNum, error: isErrored });
  }

  setExternalContextEnabled(flag) {
    this.externalContextEnabled = flag;
  }

  broadcastListeners() {
    this.messageService.on('resetPlugin').subscribe(() => {
      log.info('LineShaft restart - Resetting WFMContextPlugin status');
      this.integrationStatusService.setWFMContextPluginStatus(false);
    });
  }

  public notifyOwnerShipTransfer(examId, accessionNo, receiverUsers) {
    const data = {
      sender: this.appConfigService.getUserId(),
      receiver: receiverUsers,
      exam: {
        ids: [examId],
        accessions: [accessionNo]
      }
    };

    this.p2pNotificationService
      .notifyP2P('', 'OwnershipAcquired', data)
      .then(() => {
        log.info(
          'Ownership acquired for Exam : ' + data.exam.accessions + ', Reading by another user : ' + data.receiver
        );
      })
      .catch(error => {
        log.error('OwnershipAcquired - p2p notification failed : ', JSON.stringify(error));
      });
  }

  public notifyFailedToAcquireOwnership(accessionNo) {
    this.notify.warning(
      this.translate.instant('ownership.transfered.failed.message', {
        ACC: accessionNo
      }),
      this.translate.instant('di.desktopIntegration.title')
    );
  }

  isSMSOperationInProgress(workItem, checkWFM_Study_Management = false) {
    const SM_STATUS: number[] = [appConstants.UV_STUDY_MANAGEMENT_INPROGRESS];

    if (checkWFM_Study_Management) {
      SM_STATUS.push(appConstants.WFM_STUDY_MANAGEMENT_INPROGRESS);
    }

    return (
      SM_STATUS.includes(workItem.state.ownership?.assignedToUser[0]?.extStatusFlag) &&
      (this.integrationStatusService.isReportingType(appConstants.reportingAppName.PS) ||
        this.integrationStatusService.isReportingType(appConstants.reportingAppName.Generic))
    );
  }

  isOperationDiscarded(accNum): boolean {
    const contextId = this.reportContextService.getCurrentRequestedProcId();

    if (
      this.integrationStatusService.isReportingType(appConstants.reportingAppName.PS) ||
      this.integrationStatusService.isReportingType(appConstants.reportingAppName.Generic)
    ) {
      this.reportContextService.removeContext(contextId);
      this._removeContext.next(accNum);
      return true;
    }
    return false;
  }

  notifyUserAboutSMSInprogress(accNum) {
    this.notify.critical(
      this.translate.instant('di.desktopIntegration.StudyManagement.inprogress', {
        ACC: accNum
      }),
      this.translate.instant('di.desktopIntegration.StudyManagement.title')
    );
  }

  private registerEvents() {
    this.eventService.afterEvent('unassign', (target, state) => {
      if (this.examLinkWorkItem) {
        const newState = new StateUpdate(state.workitemId, state, this.appConfigService.getUserId()).newState;
        this.examLinkWorkItem.state = newState;
      }
    });
  }
}
